import { useEffect, useState, Dispatch, SetStateAction } from 'react';
import { useDispatch } from 'react-redux';
import { ApiBase } from '../../../api/api-base';
import { ApiDesignType1 } from '../../../api/back/create-order/design-type1/api-design-type1';
import { DesignType1Response } from '../../../models/api/back/create-order/design-type1';
import { ApiEndpaper } from '../../../api/back/create-order/endpaper/api-endpaper';
import { EndpaperResponse } from '../../../models/api/back/create-order/endpaper';
import { ApiItems } from '../../../api/back/create-order/items/api-items';
import { ItemsResponse } from '../../../models/api/back/create-order/items';
import { ApiCovers } from '../../../api/back/create-order/covers/api-covers';
import { CoversResponse } from '../../../models/api/back/create-order/covers';
import { ApiBoardInfos } from '../../../api/back/create-order/board-info/api-board-infos';
import { BoardInfosResponse } from '../../../models/api/back/create-order/board-infos';
import { ApiPageDataTypes } from '../../../api/back/create-order/page-data-types/api-page-data-types';
import { PageDataTypesResponse } from '../../../models/api/back/create-order/page-data-types';
import { apiActions } from '../../../slices/api-slice';
import { ResponseBase } from '../../../api/response-base';

// 1 注文(親 or オプション)分の名前解決ターゲット。
// 親(OrderDetailConfirmDialog)が全注文・全オプション分をまとめて渡し、ここで一括先読みする。
export type ResolveTarget = {
  productNameId?: string,
  serviceId?: string,
  productTagId?: string,
  templateGroupId?: string,
  coverId?: string,
  boardId?: string,
  coverColorId?: string,
  hasAdditionalInfo?: boolean,
  // XML 保存値(商品名)。これがある場合は商品名 API を呼ばない(XML 優先)。
  storedItemLabel?: string,
  // 記載箇所(pageType)を解決する場合のフラグ(親='1' / オプション='2')。
  pageTypeParentFlg?: '1' | '2',
};

const itemsKey = (serviceId?: string, productTagId?: string, coverTypeId?: string, coverColorId?: string) =>
  `${serviceId || ''}|${productTagId || ''}|${coverTypeId || ''}|${coverColorId || ''}`;
const coverKey = (serviceId?: string, productTagId?: string) => `${serviceId || ''}|${productTagId || ''}`;
// 記載箇所は親(parentFlg='1')/オプション(parentFlg='2')で結果が異なるためキーに含める。
const pageTypeCacheKey = (productNameId: string, parentFlg: '1' | '2') => `${productNameId}|${parentFlg}`;

// API レスポンスの配列を ID->名前 の Map に変換する(名前が空なら ID をそのまま使う)。
const buildNameMap = <T>(
  items: T[] | undefined | null,
  idOf: (item: T) => string | undefined,
  nameOf: (item: T) => string | undefined,
): Map<string, string> => {
  const map = new Map<string, string>();
  (items || []).forEach((item) => {
    const id = idOf(item);
    if (id) map.set(id, nameOf(item) || id);
  });
  return map;
};

/**
 * 注文確認ダイアログの ID -> 名前 解決フック(全注文まとめて先読み版)。
 * 親コンポーネントが全注文・全オプションのターゲットを渡し、流用できるレスポンスは
 * ここで一度だけ取得・キャッシュして全 view で共有する。
 * 名前は基本的に XML 保存値で解決し、XML に ID しか無い項目
 * (表紙選択 / 部材選択 / 記載箇所 / デザインタイプ1 / 付加情報) のみ個別 API で補う。
 * master-product.xml への依存は持たない(2023 の撤廃方針に準拠)。
 */
export const useConfirmNameResolvers = (targets: ResolveTarget[]) => {
  const dispatch = useDispatch();

  // ID -> 名前 解決用キャッシュ(キー単位で全注文共有)。
  const [designType1Cache, setDesignType1Cache] = useState<{ [productNameId: string]: Map<string, string> }>({});
  const [endpaperCache, setEndpaperCache] = useState<{ [productNameId: string]: Map<string, string> }>({});
  const [itemsCache, setItemsCache] = useState<{ [key: string]: Map<string, string> }>({});
  const [coverCache, setCoverCache] = useState<{ [key: string]: Map<string, string> }>({});
  const [boardCache, setBoardCache] = useState<{ [productNameId: string]: Map<string, string> }>({});
  const [pageTypeCache, setPageTypeCache] = useState<{ [productNameId: string]: Map<string, string> }>({});

  // ターゲットの内容が変わった時だけ先読みを走らせる(配列参照ではなく中身でガード)。
  const targetsKey = targets
    .map((t) => [
      t.productNameId, t.serviceId, t.productTagId, t.templateGroupId,
      t.coverId, t.boardId, t.coverColorId, t.hasAdditionalInfo ? '1' : '0',
      t.storedItemLabel ? '1' : '0', t.pageTypeParentFlg || '',
    ].join(':'))
    .join(',');

  useEffect(() => {
    // 同一パスで同じキーのAPIを二重発火させないための重複排除(setStateは同期forEach中に反映されないため)。
    const fired = {
      designType1: new Set<string>(),
      endpaper: new Set<string>(),
      items: new Set<string>(),
      cover: new Set<string>(),
      board: new Set<string>(),
      pageType: new Set<string>(),
    };
    // 共通: 未取得かつ未発火なら API を投げ、結果を name マップにしてキャッシュへ。
    const runResolve = (
      api: ApiBase,
      key: string,
      firedSet: Set<string>,
      cache: { [k: string]: Map<string, string> },
      setCache: Dispatch<SetStateAction<{ [k: string]: Map<string, string> }>>,
      build: (res: any) => Map<string, string>,
    ) => {
      if (cache[key] || firedSet.has(key)) return;
      firedSet.add(key);
      dispatch(apiActions.run(
        api,
        {
          onSuccess: (res: any) => setCache((prev) => ({ ...prev, [key]: build(res) })),
          onError: () => setCache((prev) => ({ ...prev, [key]: new Map() })),
        },
        { ignoreSystemError: true },
      ));
    };

    targets.forEach((t) => {
      const { productNameId, serviceId, productTagId, templateGroupId, coverId, boardId, coverColorId, hasAdditionalInfo, storedItemLabel, pageTypeParentFlg } = t;

      // デザインタイプ1
      if (productNameId && templateGroupId) {
        runResolve(new ApiDesignType1({ productNameId }), productNameId, fired.designType1, designType1Cache, setDesignType1Cache,
          (res: ResponseBase<DesignType1Response>) => buildNameMap(res.body.data?.hpsTppath, (v) => v.hpsTppathNamePath, (v) => v.hpsTppathName));
      }

      // 付加情報(endpaper)
      if (productNameId && hasAdditionalInfo) {
        runResolve(new ApiEndpaper({ productNameId, parentFlg: '1' }), productNameId, fired.endpaper, endpaperCache, setEndpaperCache,
          (res: ResponseBase<EndpaperResponse>) => buildNameMap(res.body.data?.additionalInfos, (a) => a.additionalInfoId, (a) => a.additionalInfoName));
      }

      // 商品名(XML に名前が無い時だけ)
      if (serviceId && !storedItemLabel) {
        runResolve(new ApiItems({ serviceId, productTagId, coverTypeId: coverId, coverColorId }), itemsKey(serviceId, productTagId, coverId, coverColorId), fired.items, itemsCache, setItemsCache,
          (res: ResponseBase<ItemsResponse>) => buildNameMap(res.body.data?.products, (p) => p.productNameId, (p) => p.productName));
      }

      // 表紙選択(coverId がある時だけ)
      if (serviceId && coverId) {
        runResolve(new ApiCovers({ serviceId, productTagId }), coverKey(serviceId, productTagId), fired.cover, coverCache, setCoverCache,
          (res: ResponseBase<CoversResponse>) => buildNameMap(res.body.data?.coverType, (c) => c.coverTypeId, (c) => c.coverTypeName));
      }

      // 部材選択(boardId がある時だけ)
      if (productNameId && boardId) {
        runResolve(new ApiBoardInfos({ productNameId }), productNameId, fired.board, boardCache, setBoardCache,
          (res: ResponseBase<BoardInfosResponse>) => buildNameMap(res.body.data?.boardInfo, (b) => b.boardInfoId, (b) => b.boardInfoName));
      }

      // 記載箇所(pageType)。親(parentFlg='1')とオプション(parentFlg='2')で結果が異なるため、
      // キャッシュキーに parentFlg を含める。pageDataTypes は parentFlg を問わず serviceId 必須。
      if (productNameId && pageTypeParentFlg && !!serviceId) {
        runResolve(
          new ApiPageDataTypes(pageTypeParentFlg === '1'
            ? { serviceId, productNameId, parentFlg: '1' }
            : { serviceId, productNameId, parentFlg: '2' }),
          pageTypeCacheKey(productNameId, pageTypeParentFlg), fired.pageType, pageTypeCache, setPageTypeCache,
          (res: ResponseBase<PageDataTypesResponse>) => buildNameMap(res.body.data?.pageDataTypes, (p) => p.pageDataTypeId, (p) => p.pageDataTypeDisplayName || p.pageDataTypeName));
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetsKey]);

  // 解決関数群(商品コンテキストを引数で受けて、先読み済みキャッシュから引く)。
  return {
    designType1Name: (productNameId?: string, templateGroupId?: string) =>
      (productNameId && templateGroupId ? designType1Cache[productNameId]?.get(templateGroupId) : undefined),
    resolveAdditionalInfo: (productNameId: string | undefined, id: string) =>
      (productNameId ? endpaperCache[productNameId]?.get(id) : undefined),
    hasResolvedAdditionalInfo: (productNameId?: string) =>
      Boolean(productNameId && endpaperCache[productNameId] && endpaperCache[productNameId].size > 0),
    // 商品名は XML 保存値を最優先。XML に無い場合のみ products API の結果を使う。
    resolveItemName: (serviceId: string | undefined, productTagId: string | undefined, coverId: string | undefined, coverColorId: string | undefined, productNameId: string | undefined, storedLabel?: string) =>
      storedLabel
      || (productNameId ? itemsCache[itemsKey(serviceId, productTagId, coverId, coverColorId)]?.get(productNameId) : undefined)
      || undefined,
    coverName: (serviceId: string | undefined, productTagId: string | undefined, coverId?: string) =>
      (coverId ? coverCache[coverKey(serviceId, productTagId)]?.get(coverId) : undefined),
    boardName: (productNameId: string | undefined, boardId?: string) =>
      (productNameId && boardId ? boardCache[productNameId]?.get(boardId) : undefined),
    resolvePageType: (productNameId?: string, parentFlg?: '1' | '2', pageTypeId?: string) =>
      (productNameId && parentFlg && pageTypeId ? pageTypeCache[pageTypeCacheKey(productNameId, parentFlg)]?.get(pageTypeId) : undefined),
  };
};

export type ConfirmNameResolvers = ReturnType<typeof useConfirmNameResolvers>;
